// Types and helpers for the contributions overview (per child per month). The backend calculates everything;
// these helpers only format and export.
import type { TFunction } from "i18next";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { calculateAge, parseToDate } from "../../utils/dateUtils";

export type CellStatus = "NOT_ENROLLED" | "PAID" | "PARTIAL" | "OPEN" | "UPCOMING" | "EXEMPT";

/** {@code adjusted}: a different contribution applies to this month (EXEMPT when nothing is due). */
/** skipped: the term was switched off by hand (nothing due); skipNote only reaches users who may manage adjustments. */
export type ContributionCell = {
  month: string; expected: number; paid: number; status: CellStatus; adjusted?: boolean;
  skipped?: boolean; skipNote?: string | null;
  /** Which payments fill this month, and with how much. */
  sources?: CellSource[];
};

/** Part of a payment that fills a month of a child; the date may come as [y, m, d]. */
export type CellSource = { receiptId: string; date?: unknown; amount: number };

/** Where (part of) a payment ended up: one month of one child. */
export type Placement = { childId: string; childName: string; month: string; amount: number };

/** For each payment, the months of the children it fills (read from the cells the backend calculated). */
export const placementsByReceipt = (rows: ContributionRow[]): Map<string, Placement[]> => {
  const result = new Map<string, Placement[]>();
  rows.forEach((row) => {
    (row.cells ?? []).forEach((cell) => {
      (cell.sources ?? []).forEach((source) => {
        const list = result.get(source.receiptId) ?? [];
        list.push({ childId: row.childId, childName: row.firstName || "", month: cell.month, amount: Number(source.amount) });
        result.set(source.receiptId, list);
      });
    });
  });
  result.forEach((list) => list.sort((a, b) => a.month.localeCompare(b.month) || a.childName.localeCompare(b.childName, "nl")));
  return result;
};

/**
 * A different contribution for a child during a period (months as YYYY-MM, untilMonth null = keeps applying).
 * AMOUNT: value is the monthly amount; PERCENTAGE: value percent off the fee. Reason and note only reach users who
 * may manage adjustments.
 */
export type Adjustment = {
  id: string;
  childId: string;
  fromMonth: string;
  untilMonth?: string | null;
  kind: "AMOUNT" | "PERCENTAGE";
  value: number;
  reason?: "HARDSHIP" | "STAFF" | "FUND" | "OTHER" | null;
  note?: string | null;
};

export type ContributionRow = {
  childId: string;
  firstName?: string | null;
  lastName?: string | null;
  gender?: string | null;
  dateBirth?: string | null;
  withdrawnAt?: string | null;
  familyId?: string | null;
  familyName?: string | null;
  multipleFamilies: boolean;
  cells: ContributionCell[];
  expectedToDate: number;
  expectedYear: number;
  paid: number;
  balance: number;
  adjustments?: Adjustment[];
  /** The child's class in the school year (for a withdrawn child the last one); null when not placed. */
  classroomId?: string | null;
  className?: string | null;
  classGrade?: number | null;
  /** KINDERGARTEN or GRADE. */
  classType?: string | null;
  /** Names of the class's teachers, main teacher first. */
  classTeachers?: string[];
};

export type FamilySummary = {
  familyId: string;
  familyName?: string | null;
  receiptCount: number;
  received: number;
  allocated: number;
  surplus: number;
  expectedToDate: number;
  /** Paid in the months due so far minus what is due: never above zero, money ahead for one child hides nothing. */
  balance: number;
  /** Received beyond the months due so far: in later months or not in any month. */
  ahead?: number;
};

export type SchoolYearOption = { id: string; startDate: string; endDate: string };

export type ContributionOverview = {
  schoolYears: SchoolYearOption[];
  schoolYear?: SchoolYearOption | null;
  monthlyFee?: number | null;
  months: string[];
  currentMonth: string;
  excludeCurrentMonth?: boolean;
  rows: ContributionRow[];
  families: FamilySummary[];
};

export type Receipt = {
  id: string;
  familyId: string;
  schoolYearId: string;
  date: string;
  amount: number;
  note?: string | null;
  source: "MANUAL" | "EBOEKHOUDEN";
  createdAt?: string | null;
  externalId?: string | null;
  relationName?: string | null;
  payerIbanMasked?: string | null;
  /** Who paid, as chosen by the backend; FAMILY is shown as "Family <name>". Null when unknown. */
  payerName?: string | null;
  payerNameSource?: "RELATION" | "PARENT" | "FAMILY" | "BANK_LINE" | null;
  description?: string | null;
  linkMethod?: "IBAN" | "RELATION" | "MANUAL" | "SPLIT" | null;
  ignored?: boolean;
  /** Set when the payment is meant for one child; it then fills that child's months first. */
  childId?: string | null;
  /** Parts of a divided payment (link method SPLIT). */
  allocations?: Allocation[];
  /** Term the payment is for (YYYY-MM): chosen, from the description, or the month of payment. */
  termMonth?: string | null;
  termSource?: "CHOSEN" | "DESCRIPTION" | "DATE" | null;
  /** Parts spread by hand over months (possibly in another school year); the rest is not divided. */
  termParts?: TermPart[] | null;
};

/** One child's part of a payment for one month (YYYY-MM). */
export type TermPart = { childId: string; termMonth: string; amount: number };

export type Allocation = { familyId: string; childId?: string | null; amount: number };

/** A remembered e-Boekhouden payer: always to one family, or always ask (benefactor). */
export type Payer = {
  relationId: string;
  relationName?: string | null;
  payerIbanMasked?: string | null;
  mode: "FAMILY" | "ASK";
  familyId?: string | null;
  linkMethod?: string | null;
  createdAt?: string | null;
};

export type WaitingPayments = {
  lastImportAt?: string | null;
  ledgerFilter: boolean;
  receipts: Receipt[];
  /** Previous division (scaled) for payers set to "always ask", by receipt id. */
  suggestions?: Record<string, Allocation[]>;
};

export type ImportResult = {
  from: string;
  to: string;
  found: number;
  imported: number;
  alreadyImported: number;
  linkedByIban: number;
  linkedByRelation: number;
  waiting: number;
  skipped: number;
  /** Payments waiting from earlier fetches that could now be linked by IBAN. */
  linkedLater?: number;
};

const euroFormat = new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" });
export const euro = (value: number | null | undefined): string => euroFormat.format(Number(value ?? 0));

export const monthLabel = (month: string, style: "short" | "long" = "short"): string => {
  const [year, m] = month.split("-").map(Number);
  return new Date(year, m - 1, 1).toLocaleDateString("nl-NL", style === "short" ? { month: "short" } : { month: "long", year: "numeric" })
    .replace(".", "");
};

export const schoolYearLabel = (year: SchoolYearOption): string =>
  // parseToDate: the backend may send a date as [2026, 8, 1]; only Chrome reads that with new Date (Safari: NaN).
  `${parseToDate(year.startDate)?.getFullYear() ?? "?"}–${parseToDate(year.endDate)?.getFullYear() ?? "?"}`;

/** The financial school year (1 August – 31 July) of a date, named by the year it starts in. */
export const financialStartYear = (date: Date): number => (date.getMonth() >= 7 ? date.getFullYear() : date.getFullYear() - 1);

/** The financial start year of a school year, or null when its start date cannot be read. */
export const yearStartOf = (year: SchoolYearOption): number | null => {
  const start = parseToDate(year.startDate);
  return start ? financialStartYear(start) : null;
};

/**
 * Only the current school year, the one before and the one after can be chosen; other years are not shown.
 * Matches the backend rule for manual terms.
 */
export const termWindowYears = (years: SchoolYearOption[], today: Date = new Date()): SchoolYearOption[] => {
  const current = financialStartYear(today);
  return years.filter((year) => {
    const start = yearStartOf(year);
    return start !== null && Math.abs(start - current) <= 1;
  });
};

/** The months (YYYY-MM) of the given school years, in order. */
export const monthsOfYears = (years: SchoolYearOption[]): string[] => years
  .flatMap((year) => {
    const start = yearStartOf(year);
    if (start === null) return [];
    return Array.from({ length: 12 }, (_, index) => {
      const date = new Date(start, 7 + index, 1);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    });
  })
  .sort();

export const childName = (row: ContributionRow): string => {
  const name = [row.firstName, row.lastName].filter(Boolean).join(" ");
  const age = calculateAge(row.dateBirth);
  return age === undefined ? name : `${name} (${age})`;
};

/** Months that were due but are not (fully) paid, oldest first, with what is still missing. */
export const openMonths = (cells: ContributionCell[]): Array<{ month: string; partial: boolean; missing: number }> =>
  cells
    .filter((cell) => cell.status === "OPEN" || cell.status === "PARTIAL")
    .map((cell) => ({ month: cell.month, partial: cell.status === "PARTIAL", missing: Number(cell.expected) - Number(cell.paid) }));

/** "sep, okt (€ 25 open)" — short text for lists and Excel. */
export const openMonthsText = (cells: ContributionCell[]): string =>
  openMonths(cells)
    .map((open) => (open.partial ? `${monthLabel(open.month)} (${euro(open.missing)} open)` : monthLabel(open.month)))
    .join(", ");

export const matchesContributionSearch = (row: ContributionRow, query: string): boolean => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [row.firstName, row.lastName, row.familyName].filter(Boolean).join(" ").toLowerCase();
  return needle.split(/\s+/).every((part) => haystack.includes(part));
};

/**
 * Search in a payment: payer, description (often with the family and child name), note, IBAN, amount and date.
 * Every word must occur somewhere.
 */
export const matchesReceiptSearch = (receipt: Receipt, query: string): boolean => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const amount = Number(receipt.amount);
  const date = parseToDate(receipt.date);
  const haystack = [
    receipt.relationName,
    receipt.payerName,
    receipt.description,
    receipt.note,
    receipt.payerIbanMasked,
    Number.isFinite(amount) ? amount.toFixed(2) : null,
    Number.isFinite(amount) ? amount.toFixed(2).replace(".", ",") : null,
    date ? `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}` : null,
  ].filter(Boolean).join(" ").toLowerCase();
  return needle.split(/\s+/).every((part) => haystack.includes(part));
};

/** Excel with one row per child: paid per month plus totals, like the colleague's own spreadsheet. */
export async function downloadContributionsExcel(overview: ContributionOverview, rows: ContributionRow[], t: TFunction) {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(t("finance.contributions.sheet_name", { defaultValue: "Contributions" }).slice(0, 31), {
    views: [{ state: "frozen", xSplit: 2, ySplit: 1 }],
  });
  const header = [
    t("finance.contributions.columns.child", { defaultValue: "Child" }),
    t("finance.contributions.columns.family", { defaultValue: "Family" }),
    ...overview.months.map((m) => monthLabel(m)),
    t("finance.contributions.columns.expected_to_date", { defaultValue: "Expected to date" }),
    t("finance.contributions.columns.expected_year", { defaultValue: "Expected full year" }),
    t("finance.contributions.columns.paid", { defaultValue: "Paid" }),
    t("finance.contributions.columns.balance", { defaultValue: "Balance" }),
    t("finance.contributions.columns.open_months", { defaultValue: "Open months" }),
  ];
  sheet.addRow(header);
  rows.forEach((row) => {
    sheet.addRow([
      childName(row),
      row.familyName || "",
      ...row.cells.map((cell) => (cell.status === "NOT_ENROLLED" ? null : Number(cell.paid))),
      Number(row.expectedToDate),
      Number(row.expectedYear),
      Number(row.paid),
      Number(row.balance),
      openMonthsText(row.cells),
    ]);
    // Open months red, partly paid months amber, as on the page.
    const added = sheet.lastRow;
    row.cells.forEach((cell, index) => {
      const color = cell.status === "OPEN" ? "FFFDE2E2" : cell.status === "PARTIAL" ? "FFFEF3C7" : cell.status === "EXEMPT" ? "FFEDE9FE" : null;
      if (added && color) {
        added.getCell(3 + index).fill = { type: "pattern", pattern: "solid", fgColor: { argb: color } };
      }
    });
  });
  sheet.getRow(1).font = { bold: true };
  const firstMoneyColumn = 3;
  for (let c = firstMoneyColumn; c < header.length; c += 1) {
    sheet.getColumn(c).numFmt = '"€" #,##0.00';
    sheet.getColumn(c).width = c < firstMoneyColumn + overview.months.length ? 10 : 14;
  }
  sheet.getColumn(1).width = 28;
  sheet.getColumn(2).width = 20;
  sheet.getColumn(header.length).width = 30;
  const year = overview.schoolYear ? schoolYearLabel(overview.schoolYear) : "";
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    `ouderbijdragen-${year || "export"}.xlsx`);
}
